import Maximize from "@/assets/icons/maximize.svg";
import Minimize from "@/assets/icons/minimize.svg";
import waitingVistaAssistantAnimation from "@/assets/lotties/logo_cercle_opaque.json";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEditorContext } from "@/contexts/EditorContext";
import { Tone } from "@/hooks/useGenerateAnswerWithIA";
import { cn } from "@/lib/utils";
import Lottie from "lottie-react";
import { Check, X } from "lucide-react";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export enum IAToolbarVariant {
    DEFAULT = "default",
    FULL_SCREEN = "fullScreen",
}

interface IAToolbarProps {
    className?: string;
    variant?: IAToolbarVariant;
    makeSuggestion: (action: Tone) => void;
    acceptOrRefuseSuggestion: (accept: boolean) => void;
    isLoading: boolean;
    isPendingChange: boolean;
}

interface ButtonConfig {
    label: string;
    icon: ReactNode;
    action?: Tone;
    onClick?: () => void;
}

const DEFAULT_BUTTONS = (t: (key: string) => string): ButtonConfig[] => [
    {
        label: t("aiToolbar.correct"),
        icon: <Check className="h-4 w-4" />,
        action: Tone.CORRECT,
    },
    {
        label: t("aiToolbar.extend"),
        icon: (
            <img
                alt="Maximize"
                className="brightness-0 invert"
                src={Maximize}
            />
        ),
        action: Tone.EXTEND,
    },
    {
        label: t("aiToolbar.shorten"),
        icon: (
            <img
                alt="Minimize"
                className="brightness-0 invert"
                src={Minimize}
            />
        ),
        action: Tone.SHORTCUTS,
    },
    { label: t("aiToolbar.friendly"), icon: "😊", action: Tone.FRIENDLY },
    { label: t("aiToolbar.neutral"), icon: "😐", action: Tone.NEUTRAL },
    { label: t("aiToolbar.formal"), icon: "💼", action: Tone.FORMAL },
];

const getPendingChangeButtons = (
    t: (key: string) => string,
    acceptOrRefuseSuggestion: (accept: boolean) => void,
): ButtonConfig[] => [
    {
        label: t("common.accept"),
        icon: <Check className="h-4 w-4" />,
        onClick: () => acceptOrRefuseSuggestion(true),
    },
    {
        label: t("common.refuse"),
        icon: <X className="h-4 w-4" />,
        onClick: () => acceptOrRefuseSuggestion(false),
    },
];

const IAToolbar = ({
    className,
    variant = IAToolbarVariant.DEFAULT,
    makeSuggestion,
    acceptOrRefuseSuggestion,
    isLoading,
    isPendingChange,
}: IAToolbarProps) => {
    const { t } = useTranslation();
    const { editor, pendingSuggestion } = useEditorContext();

    const [isTextSelected, setIsTextSelected] = useState(false);
    const [userForcedOpen, setUserForcedOpen] = useState(false);

    useEffect(() => {
        if (editor) {
            editor.on("selectionUpdate", () => {
                const { selection } = editor.state;
                const { from, to } = selection;
                if (from !== to) {
                    const selectedText = editor.state.doc.textBetween(
                        from,
                        to,
                        "\n",
                    );
                    if (selectedText.length > 0) {
                        setIsTextSelected(true);
                    } else {
                        setIsTextSelected(false);
                    }
                } else {
                    setIsTextSelected(false);
                }
            });
        }
    }, [editor]);

    if (!editor) return null;

    const handleButtonClick = async (action: Tone | undefined) => {
        if (pendingSuggestion || action === undefined) return;

        makeSuggestion(action);
    };

    const barExpanded = pendingSuggestion || isTextSelected || userForcedOpen;
    const buttons = pendingSuggestion
        ? getPendingChangeButtons(t, acceptOrRefuseSuggestion)
        : DEFAULT_BUTTONS(t);

    return (
        <div
            className={cn(
                "flex justify-center rounded-xl transition-all duration-300",
                barExpanded && "bg-primary-dark shadow-md",
                className,
            )}
        >
            <Button
                onClick={() => setUserForcedOpen(!userForcedOpen)}
                size="circle"
                variant="invisible"
            >
                <span
                    aria-label={t("aiToolbar.assistantToggle")}
                    className={cn(
                        "flex h-8 w-8 shrink-0 transition-transform duration-300 [&>svg]:h-full [&>svg]:w-full",
                        barExpanded || isLoading
                            ? "rotate-[360deg]"
                            : "rotate-0",
                    )}
                >
                    <Lottie
                        animationData={waitingVistaAssistantAnimation}
                        className="h-8 w-8"
                        initialSegment={barExpanded ? undefined : [1, 1]}
                        loop={barExpanded}
                    />
                </span>
            </Button>

            <div
                className={cn(
                    "flex w-45 items-center justify-center gap-2 overflow-hidden transition-all duration-300",
                    variant === IAToolbarVariant.FULL_SCREEN &&
                        (isPendingChange ? "w-60" : "w-150"),
                    variant !== IAToolbarVariant.FULL_SCREEN &&
                        (isPendingChange ? "w-20" : "w-45"),
                    !barExpanded && "w-0",
                )}
            >
                {buttons.map((btn) => {
                    const button = (
                        <Button
                            aria-label={btn.label}
                            className={cn(
                                "flex items-center gap-1 text-white hover:bg-white/20",
                                variant === IAToolbarVariant.FULL_SCREEN &&
                                    "bg-white/10",
                                isLoading && "cursor-not-allowed opacity-50",
                            )}
                            disabled={isLoading}
                            onClick={() =>
                                isPendingChange
                                    ? btn.onClick?.()
                                    : handleButtonClick(btn.action)
                            }
                            size={
                                variant === IAToolbarVariant.FULL_SCREEN
                                    ? "xs"
                                    : "tinyCircle"
                            }
                            type="button"
                            variant="invisible"
                        >
                            <span>{btn.icon}</span>
                            {variant === IAToolbarVariant.FULL_SCREEN && (
                                <span>{btn.label}</span>
                            )}
                        </Button>
                    );

                    if (variant === IAToolbarVariant.FULL_SCREEN) {
                        return <Fragment key={btn.label}>{button}</Fragment>;
                    }

                    return (
                        <TooltipProvider key={btn.label} delayDuration={100}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    {button}
                                </TooltipTrigger>
                                <TooltipContent>{btn.label}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                })}
            </div>
        </div>
    );
};

export default IAToolbar;
